import React, { useEffect, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Heart, Sparkles, Shield, ChevronDown, Leaf, BookOpen, Star, Download } from 'lucide-react';
// --- Types & Data ---
const FAQ_ITEMS = [{
  question: '¿Es un producto físico?',
  answer: 'No. Es un producto digital de descarga inmediata. Podrás acceder a él desde tu celular, tablet o computadora al instante.'
}, {
  question: '¿Necesito conocimientos previos?',
  answer: 'No. Está explicado de forma simple y clara, paso a paso, para que cualquiera pueda aplicar los consejos sin complicaciones.'
}, {
  question: '¿Para quién es este manual?',
  answer: 'Para mujeres adultas y familias que buscan soluciones naturales, prácticas y económicas para el hogar y el bienestar diario.'
}, {
  question: '¿El acceso es limitado?',
  answer: 'No. El acceso es de por vida. Una vez que lo comprás, es tuyo para siempre, incluyendo futuras actualizaciones.'
}];
const TESTIMONIALS = [{
  name: 'Mariana',
  age: 42,
  text: 'Me recordó todo lo que hacía mi abuela. Y funciona de verdad.'
}, {
  name: 'Laura',
  age: 36,
  text: 'Lo tengo guardado en el celular. Siempre vuelvo cuando necesito algo.'
}, {
  name: 'Ana',
  age: 51,
  text: 'Dejé de depender de productos caros para cosas simples.'
}];
// --- UI Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const Button = ({
  children,
  className = '',
  variant = 'primary',
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary';
}) => {
  const baseStyles = 'inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-lg';
  const variants = {
    primary: 'bg-[#D4756E] text-white hover:bg-[#C0605A] shadow-[#D4756E]/30',
    secondary: 'bg-[#FFF8F0] text-[#5D4037] border-2 border-[#D4A574] hover:bg-[#FDF6ED] shadow-[#D4A574]/20'
  };
  return <motion.button whileHover={{
    scale: 1.02,
    y: -2
  }} whileTap={{
    scale: 0.98
  }} onClick={onClick} className={`${baseStyles} ${variants[variant]} ${className}`} {...props} data-id="element-1">
      {children}
    </motion.button>;
};
const AccordionItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-[#D4A574]/30 last:border-0" data-id="element-2">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-3">
        <span className="text-xl font-medium text-[#5D4037] group-hover:text-[#D4756E] transition-colors font-['Caveat'] md:text-2xl" data-id="element-4">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="text-[#D4A574]" data-id="element-5">
          <ChevronDown className="w-6 h-6" data-id="element-6" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-7">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-8">
            <p className="pb-6 text-[#5D4037]/80 leading-relaxed" data-id="element-9">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
// --- Main Application ---
export function App() {
  // Initialize Meta Pixel on component mount
  useEffect(() => {
    // Inject Meta Pixel script
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '1200362441481134');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    // Add noscript fallback
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = 'https://www.facebook.com/tr?id=1200362441481134&ev=PageView&noscript=1';
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  }, []);
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const handleCheckout = () => {
    // Trigger Meta Pixel AddToCart event
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'El Manual Secreto de la Abuela 2.0',
        content_ids: ['44608060981302'],
        content_type: 'product',
        value: 19990,
        currency: 'ARS'
      });
    }
    // Small delay to ensure pixel fires before redirect
    setTimeout(() => {
      window.location.href = 'https://onlinepack-5.myshopify.com/cart/44608060981302:1';
    }, 300);
  };
  return <div className="min-h-screen bg-[#FFF8F0] text-[#5D4037] font-sans selection:bg-[#D4756E]/20" data-id="element-10">
      <style data-id="element-11">{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
        
        h1, h2, h3, h4, h5, h6 {
          font-family: 'Caveat', cursive;
        }
        
        body {
          font-family: 'Inter', sans-serif;
        }
      `}</style>

      {/* Announcement Bar */}
      <div className="bg-[#D4A574] text-[#FFF8F0] py-2 px-4 text-center text-sm font-medium tracking-wide" data-id="element-12">
        <p data-id="element-13">
          Oferta de lanzamiento por tiempo limitado · Acceso inmediato ·
          Garantía 30 días
        </p>
      </div>

      {/* Hero Section */}
      <section className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32 overflow-hidden" data-id="element-14">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#FDF6ED] to-transparent -z-10" data-id="element-15" />
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#D4756E]/5 rounded-full blur-3xl -z-10" data-id="element-16" />

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-17">
          <FadeIn className="order-2 md:order-1 space-y-8" data-id="element-18">
            <div className="inline-flex items-center space-x-2 bg-[#D4A574]/10 px-4 py-2 rounded-full text-[#C8956E] font-medium text-sm" data-id="element-19">
              <Sparkles className="w-4 h-4" data-id="element-20" />
              <span data-id="element-21">Sabiduría ancestral recuperada</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-[#3E2723] leading-[0.9]" data-id="element-22">
              El Manual Secreto de la{' '}
              <span className="text-[#D4756E]" data-id="element-23">Abuela 2.0</span>
            </h1>

            <p className="text-xl md:text-2xl text-[#5D4037]/80 font-light leading-relaxed" data-id="element-24">
              Todo lo que tu abuela sabía… ahora en un solo lugar. Soluciones
              simples, naturales y probadas para el hogar, el cuerpo y la vida
              cotidiana.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-25">
              <Button className="w-full sm:w-auto" onClick={scrollToPricing} data-id="element-26">
                Accedé ahora al Manual
              </Button>
            </div>

            <div className="flex items-center gap-4 text-sm text-[#5D4037]/60 pt-2" data-id="element-27">
              <div className="flex -space-x-2" data-id="element-28">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-[#D4A574]/20 border-2 border-[#FFF8F0] flex items-center justify-center text-xs font-bold text-[#D4A574]" data-id="element-29">
                    <Heart className="w-4 h-4 fill-current" data-id="element-30" />
                  </div>)}
              </div>
              <p data-id="element-31">Más de 10.000 lectoras · Descarga inmediata · 100% digital</p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} className="order-1 md:order-2 relative" data-id="element-32">
            <div className="relative z-10 rounded-3xl overflow-hidden shadow-[0_20px_50px_-12px_rgba(212,165,116,0.4)] border-8 border-white transform rotate-2 hover:rotate-0 transition-transform duration-700" data-id="element-33">
              <img src="https://cdn.magicpatterns.com/uploads/iKokkZE6wCzHGhKNdMWWc5/ChatGPT_Image_20_dic_2025,_05_20_25_p.m..png" alt="El Manual Secreto de la Abuela - Libros antiguos y remedios naturales" className="w-full h-auto object-cover" data-id="element-34" />
            </div>
            {/* Decorative elements */}
            <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[#D4A574] rounded-full opacity-20 blur-xl" data-id="element-35" />
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#D4756E] rounded-full opacity-10 blur-xl" data-id="element-36" />
          </FadeIn>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="py-20 px-4 bg-white/50 relative" data-id="element-37">
        <div className="max-w-4xl mx-auto" data-id="element-38">
          <FadeIn className="text-center mb-16" data-id="element-39">
            <h2 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-6" data-id="element-40">
              ¿Por qué todo se volvió tan complicado?
            </h2>
            <div className="w-24 h-1 bg-[#D4A574] mx-auto rounded-full" data-id="element-41" />
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-12 md:gap-20" data-id="element-42">
            <FadeIn delay={0.1} className="space-y-6" data-id="element-43">
              <div className="bg-[#FFF8F0] p-8 rounded-3xl shadow-sm border border-[#D4A574]/10" data-id="element-44">
                <h3 className="text-2xl font-bold text-[#D4756E] mb-4 flex items-center gap-2" data-id="element-45">
                  <span className="text-3xl" data-id="element-46">😓</span> El Problema
                </h3>
                <p className="text-[#5D4037]/80 mb-4" data-id="element-47">
                  Hoy estamos rodeadas de productos caros, soluciones
                  artificiales y promesas que no cumplen. Para cada problema
                  cotidiano parece que hay que comprar algo nuevo.
                </p>
                <p className="text-[#5D4037]/80 font-medium" data-id="element-48">
                  Dolores comunes. Malestares diarios. Problemas simples que se
                  vuelven complejos.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="space-y-6" data-id="element-49">
              <div className="bg-[#FFF8F0] p-8 rounded-3xl shadow-lg border border-[#D4A574]/20 relative overflow-hidden" data-id="element-50">
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#D4A574]/10 rounded-bl-full" data-id="element-51" />
                <h3 className="text-2xl font-bold text-[#C8956E] mb-4 flex items-center gap-2" data-id="element-52">
                  <span className="text-3xl" data-id="element-53">🌿</span> La Solución
                </h3>
                <p className="text-[#5D4037]/80 mb-4" data-id="element-54">
                  Mientras tanto, durante años, las abuelas resolvieron todo con
                  lo que tenían en casa. Con experiencia. Con lógica. Con
                  conocimiento real.
                </p>
                <p className="text-[#5D4037]/80 font-medium" data-id="element-55">
                  El Manual Secreto de la Abuela 2.0 recupera esa sabiduría
                  práctica y la transforma en una guía clara, moderna y fácil de
                  aplicar.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4" data-id="element-56">
        <div className="max-w-6xl mx-auto" data-id="element-57">
          <FadeIn className="text-center mb-16" data-id="element-58">
            <h2 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-6" data-id="element-59">
              ¿Qué vas a encontrar adentro?
            </h2>
            <p className="text-xl text-[#5D4037]/70 max-w-2xl mx-auto" data-id="element-60">
              Todo explicado paso a paso, sin términos raros ni complicaciones.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-61">
            {[{
            icon: <Leaf className="w-8 h-8 text-[#D4756E]" data-id="element-62" />,
            title: 'Remedios Naturales',
            desc: 'Caseros para molestias comunes, defensas bajas, resfríos, digestión, piel y bienestar general.'
          }, {
            icon: <Sparkles className="w-8 h-8 text-[#C8956E]" data-id="element-63" />,
            title: 'Hogar y Limpieza',
            desc: 'Trucos simples, económicos y efectivos para mantener tu casa impecable sin químicos agresivos.'
          }, {
            icon: <Heart className="w-8 h-8 text-[#D4A574]" data-id="element-64" />,
            title: 'Cuidado Personal',
            desc: 'Consejos prácticos para el cuidado del cuerpo, el descanso y la rutina diaria.'
          }].map((item, idx) => <FadeIn key={idx} delay={idx * 0.1} className="bg-white p-8 rounded-3xl shadow-[0_10px_30px_-10px_rgba(212,165,116,0.2)] hover:shadow-[0_15px_40px_-10px_rgba(212,165,116,0.3)] transition-shadow border border-[#FFF8F0]" data-id="element-65">
                <div className="w-16 h-16 bg-[#FFF8F0] rounded-2xl flex items-center justify-center mb-6" data-id="element-66">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-bold text-[#3E2723] mb-4" data-id="element-67">
                  {item.title}
                </h3>
                <p className="text-[#5D4037]/80 leading-relaxed" data-id="element-68">{item.desc}</p>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.4} className="mt-16 bg-[#D4A574]/10 rounded-3xl p-8 md:p-12 text-center max-w-3xl mx-auto border border-[#D4A574]/20" data-id="element-69">
            <h3 className="text-3xl font-bold text-[#3E2723] mb-4" data-id="element-70">
              Cómo funciona
            </h3>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 text-left md:text-center" data-id="element-71">
              <div className="flex-1" data-id="element-72">
                <span className="block text-4xl font-bold text-[#D4756E]/40 mb-2" data-id="element-73">
                  01
                </span>
                <p className="font-medium" data-id="element-74">Descargás el manual</p>
              </div>
              <div className="hidden md:block text-[#D4A574]" data-id="element-75">→</div>
              <div className="flex-1" data-id="element-76">
                <span className="block text-4xl font-bold text-[#D4756E]/40 mb-2" data-id="element-77">
                  02
                </span>
                <p className="font-medium" data-id="element-78">Buscás el problema</p>
              </div>
              <div className="hidden md:block text-[#D4A574]" data-id="element-79">→</div>
              <div className="flex-1" data-id="element-80">
                <span className="block text-4xl font-bold text-[#D4756E]/40 mb-2" data-id="element-81">
                  03
                </span>
                <p className="font-medium" data-id="element-82">Aplicás la solución</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-[#D4A574]/5" data-id="element-83">
        <div className="max-w-6xl mx-auto" data-id="element-84">
          <FadeIn className="text-center mb-16" data-id="element-85">
            <h2 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-6" data-id="element-86">
              Lo que dicen nuestras lectoras
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-87">
            {TESTIMONIALS.map((t, idx) => <FadeIn key={idx} delay={idx * 0.1} className="bg-white p-8 rounded-3xl shadow-sm relative" data-id="element-88">
                <div className="absolute -top-4 left-8 text-6xl text-[#D4A574]/20 font-serif" data-id="element-89">
                  "
                </div>
                <p className="text-lg text-[#5D4037] mb-6 italic relative z-10" data-id="element-90">
                  {t.text}
                </p>
                <div className="flex items-center gap-3" data-id="element-91">
                  <div className="w-10 h-10 rounded-full bg-[#FFF8F0] flex items-center justify-center text-[#D4756E] font-bold" data-id="element-92">
                    {t.name[0]}
                  </div>
                  <div data-id="element-93">
                    <p className="font-bold text-[#3E2723]" data-id="element-94">{t.name}</p>
                    <p className="text-sm text-[#5D4037]/60" data-id="element-95">{t.age} años</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Expert Endorsement */}
      <section className="py-16 px-4" data-id="element-96">
        <div className="max-w-4xl mx-auto" data-id="element-97">
          <FadeIn className="bg-[#3E2723] text-[#FFF8F0] rounded-3xl p-8 md:p-12 relative overflow-hidden text-center" data-id="element-98">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/wood-pattern.png')]" data-id="element-99"></div>
            <Star className="w-12 h-12 text-[#D4A574] mx-auto mb-6" data-id="element-100" />
            <blockquote className="text-2xl md:text-3xl font-medium leading-relaxed mb-6 font-['Caveat']" data-id="element-101">
              "La sabiduría popular bien aplicada puede ser más efectiva que
              muchas soluciones modernas."
            </blockquote>
            <cite className="not-italic text-[#D4A574] font-medium tracking-wide uppercase text-sm" data-id="element-102">
              Especialista en bienestar y hábitos saludables
            </cite>
          </FadeIn>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-gradient-to-b from-[#FFF8F0] to-[#FDF6ED] relative overflow-hidden scroll-mt-4" data-id="element-103">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4756E]/10 rounded-full blur-3xl" data-id="element-104" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4A574]/10 rounded-full blur-3xl" data-id="element-105" />

        <div className="max-w-6xl mx-auto" data-id="element-106">
          <FadeIn className="text-center mb-12" data-id="element-107">
            <div className="inline-block bg-[#D4756E]/10 px-6 py-2 rounded-full mb-4" data-id="element-108">
              <span className="text-[#D4756E] font-bold uppercase text-sm tracking-wider" data-id="element-109">
                🎁 Oferta Especial de Lanzamiento
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-4" data-id="element-110">
              Accedé hoy al precio más bajo
            </h2>
            <p className="text-xl text-[#5D4037]/70" data-id="element-111">
              Disponible en todos tus dispositivos
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-112">
            <FadeIn delay={0.1} className="relative" data-id="element-113">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-[0_25px_60px_-15px_rgba(212,117,110,0.4)] transform hover:scale-[1.02] transition-transform duration-500" data-id="element-114">
                <img src="https://cdn.magicpatterns.com/uploads/b2PZg3SF5rdpZXeGgbWB9i/ChatGPT_Image_20_dic_2025,_05_20_25_p.m..png" alt="El Manual Secreto de la Abuela 2.0 en todos tus dispositivos" className="w-full h-auto" data-id="element-115" />
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#D4A574]/20 rounded-full blur-2xl -z-10" data-id="element-116" />
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-[#D4756E]/20 rounded-full blur-2xl -z-10" data-id="element-117" />
            </FadeIn>

            <FadeIn delay={0.2} className="space-y-8" data-id="element-118">
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-[#D4A574]/20" data-id="element-119">
                <div className="text-center mb-8" data-id="element-120">
                  <p className="text-[#5D4037]/60 text-lg mb-2 line-through" data-id="element-121">
                    Precio regular: $39.990
                  </p>
                  <div className="flex items-baseline justify-center gap-2" data-id="element-122">
                    <span className="text-6xl md:text-7xl font-bold text-[#D4756E] font-['Caveat']" data-id="element-123">
                      $19.990
                    </span>
                  </div>
                  <p className="text-[#5D4037]/70 mt-2 font-medium" data-id="element-124">
                    Pago único • Acceso de por vida
                  </p>
                </div>

                <div className="space-y-4 mb-8" data-id="element-125">
                  {['Descarga inmediata', 'Acceso en todos tus dispositivos', 'Actualizaciones gratuitas', 'Garantía de 30 días', 'Bono: Guía de emergencias'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-126">
                      <div className="w-6 h-6 rounded-full bg-[#D4756E]/20 flex items-center justify-center flex-shrink-0" data-id="element-127">
                        <Check className="w-4 h-4 text-[#D4756E]" data-id="element-128" />
                      </div>
                      <span className="text-[#5D4037]" data-id="element-129">{item}</span>
                    </div>)}
                </div>

                <Button className="w-full text-xl py-6 shadow-2xl shadow-[#D4756E]/30" onClick={handleCheckout} data-id="element-130">
                  Comprar ahora por $19.990
                </Button>

                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-[#5D4037]/60" data-id="element-131">
                  <Shield className="w-4 h-4 text-[#D4A574]" data-id="element-132" />
                  <span data-id="element-133">Pago seguro • Garantía de devolución</span>
                </div>
              </div>

              <div className="bg-[#D4A574]/10 rounded-2xl p-6 border border-[#D4A574]/30 text-center" data-id="element-134">
                <p className="text-[#5D4037] font-medium" data-id="element-135">
                  ⏰{' '}
                  <span className="font-bold text-[#D4756E]" data-id="element-136">
                    Últimas 24 horas
                  </span>{' '}
                  al precio de lanzamiento
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Free Bonus Section - El Botiquín Invisible */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#6B7456] to-[#5A6347] relative overflow-hidden" data-id="element-137">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/old-map.png')]" data-id="element-138"></div>
        <div className="absolute top-10 right-10 w-64 h-64 bg-[#D4A574]/10 rounded-full blur-3xl" data-id="element-139" />
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-[#8B9474]/10 rounded-full blur-3xl" data-id="element-140" />

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-141">
          <FadeIn className="text-center mb-12" data-id="element-142">
            <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="inline-block relative mb-6" data-id="element-143">
              <div className="absolute -inset-4 bg-[#D4A574] rounded-full blur-xl opacity-30" data-id="element-144"></div>
              <div className="relative bg-[#D4A574] text-[#3E2723] px-8 py-3 rounded-full font-bold text-lg uppercase tracking-wider shadow-2xl border-4 border-[#FFF8F0]" data-id="element-145">
                🎁 Bono Exclusivo GRATIS
              </div>
            </motion.div>
            <h2 className="text-4xl md:text-6xl font-bold text-[#FFF8F0] mb-4 font-['Caveat']" data-id="element-146">
              Recibís también sin costo adicional
            </h2>
            <p className="text-xl text-[#FFF8F0]/80" data-id="element-147">
              Un regalo especial para las primeras compradoras
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-148">
            <FadeIn delay={0.1} className="relative order-2 md:order-1" data-id="element-149">
              <div className="bg-[#FFF8F0] rounded-3xl p-8 md:p-10 shadow-2xl border-4 border-[#D4A574]/30" data-id="element-150">
                <div className="mb-6" data-id="element-151">
                  <h3 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-3 font-['Caveat']" data-id="element-152">
                    El Botiquín Invisible de la Abuela
                  </h3>
                  <div className="w-20 h-1 bg-[#6B7456] rounded-full" data-id="element-153"></div>
                </div>

                <p className="text-lg text-[#5D4037]/80 mb-6 leading-relaxed" data-id="element-154">
                  Una guía completa con los remedios naturales más poderosos que
                  tu abuela guardaba como secretos. Soluciones ancestrales para
                  malestares comunes que funcionan de verdad.
                </p>

                <div className="space-y-4 mb-8" data-id="element-155">
                  <h4 className="font-bold text-[#3E2723] text-lg mb-4" data-id="element-156">
                    Incluye:
                  </h4>
                  {['Remedios para dolores de cabeza y migrañas', 'Soluciones naturales para problemas digestivos', 'Tratamientos para resfríos y gripes', 'Recetas para fortalecer las defensas', 'Preparados para el cuidado de la piel', 'Infusiones curativas y sus propiedades'].map((item, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: i * 0.1
                }} className="flex items-start gap-3" data-id="element-157">
                      <div className="mt-1 w-6 h-6 rounded-full bg-[#6B7456]/20 flex items-center justify-center flex-shrink-0" data-id="element-158">
                        <Check className="w-4 h-4 text-[#6B7456]" data-id="element-159" />
                      </div>
                      <span className="text-[#5D4037]" data-id="element-160">{item}</span>
                    </motion.div>)}
                </div>

                <div className="bg-[#6B7456]/10 rounded-2xl p-6 border-2 border-[#6B7456]/30" data-id="element-161">
                  <div className="flex items-center justify-between" data-id="element-162">
                    <div data-id="element-163">
                      <p className="text-sm text-[#5D4037]/60 mb-1" data-id="element-164">
                        Valor del bono:
                      </p>
                      <p className="text-3xl font-bold text-[#6B7456] font-['Caveat']" data-id="element-165">
                        $9.990
                      </p>
                    </div>
                    <div className="text-right" data-id="element-166">
                      <p className="text-sm text-[#5D4037]/60 mb-1" data-id="element-167">
                        Tu precio:
                      </p>
                      <p className="text-4xl font-bold text-[#D4756E] font-['Caveat']" data-id="element-168">
                        GRATIS
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="relative order-1 md:order-2" data-id="element-169">
              <motion.div initial={{
              rotate: -3,
              scale: 0.95
            }} whileInView={{
              rotate: 0,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.7,
              ease: 'easeOut'
            }} className="relative" data-id="element-170">
                <div className="relative z-10 rounded-3xl overflow-hidden shadow-[0_30px_70px_-15px_rgba(0,0,0,0.5)] border-8 border-[#D4A574]" data-id="element-171">
                  <img src="https://cdn.magicpatterns.com/uploads/u4YZMdg7qnGMxKUmbgigbT/ChatGPT_Image_21_dic_2025,_06_09_59_p.m..png" alt="El Botiquín Invisible de la Abuela - Remedios naturales ancestrales" className="w-full h-auto" data-id="element-172" />
                </div>
                {/* Decorative glow effects */}
                <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-[#D4A574] rounded-full opacity-20 blur-3xl" data-id="element-173" />
                <div className="absolute -top-8 -left-8 w-40 h-40 bg-[#8B9474] rounded-full opacity-20 blur-3xl" data-id="element-174" />
              </motion.div>
            </FadeIn>
          </div>

          <FadeIn delay={0.3} className="mt-12 text-center" data-id="element-175">
            <div className="inline-block bg-[#FFF8F0]/10 backdrop-blur-sm rounded-2xl px-8 py-4 border border-[#FFF8F0]/20" data-id="element-176">
              <p className="text-[#FFF8F0] text-lg" data-id="element-177">
                ✨ <span className="font-bold" data-id="element-178">Solo por tiempo limitado:</span>{' '}
                Este bono se incluye GRATIS con tu compra hoy
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Offer Section */}
      <section className="py-20 px-4 relative overflow-hidden" data-id="element-179">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#D4756E]/5 rounded-full blur-3xl -z-10" data-id="element-180" />

        <div className="max-w-5xl mx-auto bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-[#D4A574]/20" data-id="element-181">
          <div className="grid md:grid-cols-2" data-id="element-182">
            <div className="p-8 md:p-16 space-y-8" data-id="element-183">
              <h2 className="text-4xl font-bold text-[#3E2723]" data-id="element-184">
                Empezá hoy mismo
              </h2>

              <ul className="space-y-4" data-id="element-185">
                {['El Manual Secreto de la Abuela 2.0 completo', 'Formato digital descargable', 'Acceso de por vida', 'Actualizaciones incluidas'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-186">
                    <div className="mt-1 w-5 h-5 rounded-full bg-[#D4756E]/20 flex items-center justify-center flex-shrink-0" data-id="element-187">
                      <Check className="w-3 h-3 text-[#D4756E]" data-id="element-188" />
                    </div>
                    <span className="text-[#5D4037]" data-id="element-189">{item}</span>
                  </li>)}
              </ul>

              <div className="bg-[#FFF8F0] p-6 rounded-2xl border border-[#D4A574]/30" data-id="element-190">
                <div className="flex items-center gap-2 mb-2" data-id="element-191">
                  <Sparkles className="w-5 h-5 text-[#D4A574]" data-id="element-192" />
                  <span className="font-bold text-[#C8956E] uppercase text-sm tracking-wider" data-id="element-193">
                    Bono de lanzamiento
                  </span>
                </div>
                <p className="font-medium text-[#3E2723]" data-id="element-194">
                  Guía rápida "10 remedios de abuela para emergencias
                  cotidianas"
                </p>
              </div>

              <div className="pt-4" data-id="element-195">
                <p className="text-sm text-[#5D4037]/60 mb-4 text-center md:text-left" data-id="element-196">
                  Precio especial disponible solo por tiempo limitado.
                </p>
                <Button className="w-full text-xl py-5 shadow-xl shadow-[#D4756E]/20" onClick={scrollToPricing} data-id="element-197">
                  Acceder ahora al Manual
                </Button>
              </div>
            </div>

            <div className="bg-[#FDF6ED] p-8 md:p-16 flex flex-col justify-center items-center text-center relative" data-id="element-198">
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/paper.png')] opacity-50 mix-blend-multiply" data-id="element-199" />
              <div className="relative z-10" data-id="element-200">
                <Shield className="w-16 h-16 text-[#D4A574] mx-auto mb-6" data-id="element-201" />
                <h3 className="text-3xl font-bold text-[#3E2723] mb-4" data-id="element-202">
                  Garantía de 30 días
                </h3>
                <p className="text-[#5D4037]/80 mb-6 leading-relaxed" data-id="element-203">
                  Probalo sin riesgo. Tenés 30 días para leerlo y usarlo. Si no
                  es para vos, te devolvemos el dinero sin preguntas.
                </p>
                <p className="font-['Caveat'] text-2xl text-[#D4756E]" data-id="element-204">
                  El riesgo es nuestro.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4" data-id="element-205">
        <div className="max-w-3xl mx-auto" data-id="element-206">
          <FadeIn className="text-center mb-12" data-id="element-207">
            <h2 className="text-4xl font-bold text-[#3E2723] mb-4" data-id="element-208">
              Preguntas Frecuentes
            </h2>
          </FadeIn>

          <div className="space-y-2" data-id="element-209">
            {FAQ_ITEMS.map((item, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-210">
                <AccordionItem question={item.question} answer={item.answer} data-id="element-211" />
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 text-center bg-[#3E2723] text-[#FFF8F0] relative overflow-hidden" data-id="element-212">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/wood-pattern.png')]" data-id="element-213"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-black/20" data-id="element-214"></div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-215">
          <FadeIn data-id="element-216">
            <h2 className="text-5xl md:text-7xl font-bold mb-8 text-[#D4A574]" data-id="element-217">
              Volvé a lo simple. <br data-id="element-218" />
              <span className="text-white" data-id="element-219">
                Volvé a lo que siempre funcionó.
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-[#FFF8F0]/80 mb-12 max-w-2xl mx-auto font-light" data-id="element-220">
              Accedé hoy al Manual Secreto de la Abuela 2.0 y empezá a resolver
              lo cotidiano de forma natural.
            </p>
            <Button variant="primary" className="text-xl px-12 py-5 bg-[#D4A574] hover:bg-[#C8956E] text-[#3E2723] shadow-none" onClick={scrollToPricing} data-id="element-221">
              Descargar ahora <Download className="ml-2 w-5 h-5" data-id="element-222" />
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2D1B18] text-[#FFF8F0]/40 py-12 px-4 text-center text-sm" data-id="element-223">
        <div className="max-w-6xl mx-auto space-y-6" data-id="element-224">
          <div className="flex justify-center gap-8" data-id="element-225">
            <a href="#" className="hover:text-[#D4A574] transition-colors" data-id="element-226">
              Política de privacidad
            </a>
            <a href="#" className="hover:text-[#D4A574] transition-colors" data-id="element-227">
              Términos y condiciones
            </a>
          </div>
          <p className="max-w-2xl mx-auto" data-id="element-228">
            Este producto no reemplaza asesoramiento médico profesional. Utiliza
            este copy no lo cambies ni alteres la carta de ventas.
          </p>
          <p data-id="element-229">
            © {new Date().getFullYear()} El Manual Secreto de la Abuela 2.0
          </p>
        </div>
      </footer>
    </div>;
}